import {
  PROMPT_BANNER_MAX_WIDTH,
  PromptBannerCard,
  listingPromptBannerSharedInnards,
  usePromptBannerPrimaryButtonStyle,
  useMintListingPromptMinHeight,
} from 'app/components/prompt-banner-card';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { useState } from 'react';
import { isWhiteLabel } from 'app/utils/helpers';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';

import { Button } from 'app/design/primitives/button';
import { useAtomValue } from 'jotai/index';
import { isLoggedInAtom } from 'app/atoms';
import { Trans, useTranslation } from 'react-i18next';
import { ProgressBar } from 'app/components/progress-bar';
import { track, Event } from 'app/lib/analytics';
import { useUser } from 'app/network/user';
import { useProfile } from 'app/network/profile';
import { StorageKey, useStoredValue } from 'app/lib/storage';
import { useUISize } from 'app/utils/web-helpers';
import { Pressable } from 'react-native';
import { PromptPhoneNumber } from './prompt-phone-number';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconAlertTriangle, IconX } from '@tabler/icons-react-native';
const theme = createDSTheme();

export function useShowPromptPhoneNumber() {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const { data: user } = useUser();
  const { data: profile } = useProfile(user?.id);

  const [bannerAlreadySeen] = useStoredValue<boolean>(StorageKey.PromptPhoneNumberBannerSeen, false);
  const showPrompt = (() => {
    return isLoggedIn && profile && !profile.phone && !bannerAlreadySeen;
  })();

  return showPrompt;
}

function PromptPhoneBanner({ fullWidth }: { fullWidth?: boolean }) {
  const { t } = useTranslation('all-listings');
  const { data: user } = useUser();
  const { isSmallUI } = useUISize();
  const minHeight = useMintListingPromptMinHeight();
  const primaryButtonStyle = usePromptBannerPrimaryButtonStyle();
  const [show, setShow] = useState(true);

  const percent = 60;
  const showPrompt = useShowPromptPhoneNumber();
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [, setBannerAlreadySeen] = useStoredValue<boolean>(StorageKey.PromptPhoneNumberBannerSeen, false);

  const onPress = () => {
    track(Event.ClickedPromptPhoneNumber, {
      user_id: user?.id ?? '',
    });
    setIsOverlayOpen(true);
  };

  const onDismiss = () => {
    setBannerAlreadySeen(true);
    setShow(false);
  };

  if (isWhiteLabel()) return null;
  if (!showPrompt) return null;
  if (!show) return null;

  const successFg = theme.colors['text.success'];

  return (
    <Column
      alignSelf={fullWidth || isSmallUI ? 'stretch' : 'flex-start'}
      maxWidth={fullWidth || isSmallUI ? undefined : PROMPT_BANNER_MAX_WIDTH}
      position="relative"
      width="100%"
    >
      <PromptBannerCard
        {...listingPromptBannerSharedInnards}
        backgroundColor={theme.colors['surface.success.subtle']}
        style={{ minHeight, ...(fullWidth ? { maxWidth: '100%', minWidth: 0, alignSelf: 'stretch' } : {}) }}
      >
        <Row alignItems="center" gap="spacing.general.x1" paddingRight="spacing.general.x12">
          <IconAlertTriangle strokeWidth={ICON_STROKE_WIDTH} color={successFg} />
          <Text flex={1} fontWeight="700" color="text.success" variant="bodyBase">
            <Trans i18nKey="addPhoneNumberPrompt.banner.title" ns="all-listings" values={{ percent: percent }} />
          </Text>
        </Row>
        <ProgressBar
          height={8}
          backgroundColor={successFg}
          progressColor={theme.colors['text.brand']}
          percentage={percent}
        />
        <Text variant="bodyBase">
          <Trans
            components={{ strong: <Text fontWeight="700" /> }}
            i18nKey="addPhoneNumberPrompt.banner.text"
            ns="all-listings"
          />
        </Text>
        <Button onPress={onPress} style={primaryButtonStyle}>
          <Text color="surface.default" variant="bodyBase">
            {t('addPhoneNumberPrompt.banner.button')}
          </Text>
        </Button>
      </PromptBannerCard>
      <Pressable
        accessibilityRole="button"
        hitSlop={12}
        onPress={onDismiss}
        style={{
          position: 'absolute',
          right: theme.spacing['spacing.general.x2'],
          top: theme.spacing['spacing.general.x2'],
          zIndex: 2,
        }}
      >
        <IconX strokeWidth={ICON_STROKE_WIDTH} size={24} color={successFg} />
      </Pressable>
      <PromptPhoneNumber open={isOverlayOpen} onClose={() => setIsOverlayOpen(false)} />
    </Column>
  );
}

export { PromptPhoneBanner };
